import {
  Component,
  inject,
  input,
  OnInit,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { JssNavItem } from '@core/jss-models';
import {
  CrntButton,
  CrntIcon,
  CrntVisuallyHidden,
} from '@essent/current-web-angular';
import { NavigationServiceButtonComponent } from '@core/navigation-ui/navigation-service-button';
import { NavigationDomainLinksComponent } from '@core/navigation-ui/navigation-domain-links';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { TrackLinkDirective } from '@analytics/events';

import { NavigationMobileItemComponent } from '../navigation-mobile-item/navigation-mobile-item.component';

@Component({
  selector: 'wl-navigation-mobile',
  standalone: true,
  templateUrl: './navigation-mobile.component.html',
  styleUrls: [
    './navigation-mobile.component.ed.scss',
    './navigation-mobile.component.essent.scss',
  ],
  imports: [
    CrntButton,
    CrntIcon,
    NavigationMobileItemComponent,
    NavigationServiceButtonComponent,
    NavigationDomainLinksComponent,
    CrntVisuallyHidden,
    AutomationIdDirective,
    TrackLinkDirective,
  ],
})
export class NavigationMobileComponent implements OnInit {
  /**
   * The main navigation tree.
   */
  readonly navigationItems = input.required<JssNavItem[]>();
  /**
   * The text used for the button to open the offcanvas menu.
   */
  readonly openMenuButtonText = input.required<string>();
  /**
   * The text used for the button to close the offcanvas menu.
   */
  readonly closeMenuButtonText = input.required<string>();
  /**
   * The title of the menu that is displayed. This should be mapped to any ARIA
   * labels and descriptions that are used in the menu.
   */
  readonly menuTitle = input.required<string>();
  /**
   * The position from which the offcanvas menu can slide in.
   * @see https://ng-bootstrap.github.io/#/components/offcanvas/api#NgbOffcanvasOptions
   */
  readonly position = input<'start' | 'end'>('start');
  /**
   * Disable scrolling of content when the offcanvas is enabled. Defaults to
   * `start`.
   * @see https://ng-bootstrap.github.io/#/components/offcanvas/api#NgbOffcanvasOptions
   */
  readonly scroll = input(false);
  /**
   * The anchoring container for the offcanvas. In order to have proper
   * autofocus on the panel this needs to be set to the parent component of the
   * offcanvas menu. Which should be the default `wl-navigation-mobile`.
   * @see https://ng-bootstrap.github.io/#/components/offcanvas/api#NgbOffcanvasOptions
   */
  readonly container = input('wl-navigation-mobile');
  /**
   * Whether the menu should be opened by default. This is only used for testing
   * purposes in Storybook.
   */
  readonly openedByDefault = input(false);
  /**
   * Input properties for the optional login button child component.
   */
  readonly loginButton = input<{
    url?: string | undefined;
    text?: string | undefined;
  }>({});
  /**
   * Input properties for the optional domain navigation links child component.
   */
  readonly domainNavigation = input<JssNavItem[]>();
  /**
   * The reference to the embedded template used for the NgbOffcanvas service.
   */
  readonly contentElement = viewChild<TemplateRef<any>>('content');

  readonly #offcanvasService = inject(NgbOffcanvas);

  ngOnInit() {
    if (this.openedByDefault()) {
      this.open(this.contentElement());
    }
  }

  open(content: TemplateRef<any> | undefined) {
    if (!this.#offcanvasService.hasOpenOffcanvas()) {
      this.#offcanvasService.open(content, {
        container: this.container(),
        position: this.position(),
        scroll: this.scroll(),
        ariaLabelledBy: this.menuTitle(),
      });
    }
  }

  dismiss(reason: 'navigate' | 'close') {
    if (this.#offcanvasService.hasOpenOffcanvas()) {
      this.#offcanvasService.dismiss(reason);
    }
  }
}
